'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { getActivePositions } from '@/lib/services/positionService';
import { trackEvent } from '@/lib/analytics';
import { deliverForm, formEndpoints } from '@/lib/forms';
import { company } from '@/lib/company';

// Default positions fallback
const DEFAULT_POSITIONS = [
  { value: 'frontend-developer', label: 'Frontend Developer' },
  { value: 'backend-developer', label: 'Backend Developer' },
  { value: 'fullstack-developer', label: 'Full Stack Developer' },
  { value: 'mobile-developer', label: 'Mobile Developer' },
  { value: 'ui-ux-designer', label: 'UI/UX Designer' },
  { value: 'graphic-designer', label: 'Graphic Designer' },
  { value: 'project-manager', label: 'Project Manager' },
  { value: 'marketing', label: 'Marketing Specialist' },
  { value: 'other', label: 'Other' },
];

// Custom URL validation that accepts common formats
const urlSchema = z.string()
  .transform((val) => {
    if (!val || val.trim() === '') return '';
    // Add https:// if no protocol is specified
    if (val && !val.match(/^https?:\/\//i)) {
      return `https://${val}`;
    }
    return val;
  })
  .refine((val) => {
    if (!val || val === '') return true;
    try {
      new URL(val);
      return true;
    } catch {
      return false;
    }
  }, 'Please enter a valid URL');

const applySchema = z.object({
  position: z.string().min(1, 'Please select a position'),
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  linkedin: urlSchema.optional().or(z.literal('')),
  portfolio: urlSchema.optional().or(z.literal('')),
  message: z.string().optional(),
});

type ApplyFormData = z.infer<typeof applySchema>;

export function ApplyForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [channel, setChannel] = useState<'mailto' | 'endpoint'>('mailto');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [cvError, setCvError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [positions, setPositions] = useState<{ value: string; label: string }[]>(DEFAULT_POSITIONS);

  // Fetch positions from Firestore
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const data = await getActivePositions();
        if (data.length > 0) {
          setPositions(data);
        }
      } catch (error) {
        console.error('Error fetching positions:', error);
        // Keep using default positions on error
      }
    };

    fetchPositions();
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ApplyFormData>({
    resolver: zodResolver(applySchema),
  });

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const validateFile = (file: File): boolean => {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      setCvError('Please upload a PDF or Word document');
      return false;
    }

    if (file.size > maxSize) {
      setCvError('File size must be less than 5MB');
      return false;
    }

    setCvError(null);
    return true;
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && validateFile(file)) {
      setCvFile(file);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      setCvFile(file);
    }
  };

  const removeFile = () => {
    setCvFile(null);
    setCvError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data: ApplyFormData) => {
    if (!cvFile) {
      setCvError('Please attach your CV');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setUploadProgress(0);

    try {
      // The CV is deliberately NOT uploaded from the browser. The DataCode-era
      // form put it straight into that project's Firebase Storage bucket;
      // on this fork that would mean sending a candidate's personal document
      // to a third party's storage. Instead the application is composed as an
      // email the candidate sends themselves, with the CV attached by hand —
      // unless this deployment has its own endpoint configured.
      setUploadProgress(60);

      const result = await deliverForm({
        endpoint: formEndpoints.apply,
        subject: `Application: ${data.position} — ${data.name}`,
        fields: [
          ['Position', data.position],
          ['Name', data.name],
          ['Email', data.email],
          ['Phone', data.phone],
          ['LinkedIn', data.linkedin],
          ['Portfolio', data.portfolio],
          ['Message', data.message],
          ['CV', `${cvFile.name} — please attach this file before sending.`],
        ],
        payload: { ...data, cvFileName: cvFile.name },
      });

      setUploadProgress(100);
      setChannel(result.channel);

      if (result.ok) {
        setSubmitStatus('success');
        trackEvent('apply_submit', { status: 'success', position: data.position, channel: result.channel });
        reset();
        setCvFile(null);
      } else {
        setSubmitStatus('error');
        trackEvent('apply_submit', { status: 'error', position: data.position, channel: result.channel });
      }
    } catch (error) {
      console.error('Application submission error:', error);
      setSubmitStatus('error');
      trackEvent('apply_submit', { status: 'error', position: data.position });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Position Selection */}
      <div style={{ marginBottom: '48px' }}>
        <label className="block text-xs uppercase tracking-[0.3em] text-white/40" style={{ marginBottom: '16px' }}>
          Position You&apos;re Applying For *
        </label>
        <select
          {...register('position')}
          className={cn(
            'w-full border-b bg-transparent text-base text-white outline-none transition-colors appearance-none cursor-pointer',
            errors.position ? 'border-red-500' : 'border-white/30 focus:border-white'
          )}
          style={{
            paddingTop: '16px',
            paddingBottom: '16px',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 0 center',
            backgroundSize: '20px'
          }}
        >
          <option value="" className="bg-[#173835] text-white">Select a position</option>
          {positions.map((pos) => (
            <option key={pos.value} value={pos.value} className="bg-[#173835] text-white">
              {pos.label}
            </option>
          ))}
        </select>
        {errors.position && (
          <p className="mt-2 text-sm text-red-500">{errors.position.message}</p>
        )}
      </div>

      {/* Name and Email - Two columns */}
      <div className="grid md:grid-cols-2" style={{ gap: '48px', marginBottom: '48px' }}>
        <div>
          <label className="block text-xs uppercase tracking-[0.3em] text-white/40" style={{ marginBottom: '16px' }}>
            Full Name *
          </label>
          <input
            {...register('name')}
            type="text"
            placeholder="John Doe"
            className={cn(
              'w-full border-b bg-transparent text-base text-white outline-none transition-colors placeholder:text-white/30',
              errors.name ? 'border-red-500' : 'border-white/30 focus:border-white'
            )}
            style={{ paddingTop: '16px', paddingBottom: '16px' }}
          />
          {errors.name && (
            <p className="mt-2 text-sm text-red-500">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.3em] text-white/40" style={{ marginBottom: '16px' }}>
            Email Address *
          </label>
          <input
            {...register('email')}
            type="email"
            placeholder="john@example.com"
            className={cn(
              'w-full border-b bg-transparent text-base text-white outline-none transition-colors placeholder:text-white/30',
              errors.email ? 'border-red-500' : 'border-white/30 focus:border-white'
            )}
            style={{ paddingTop: '16px', paddingBottom: '16px' }}
          />
          {errors.email && (
            <p className="mt-2 text-sm text-red-500">{errors.email.message}</p>
          )}
        </div>
      </div>

      {/* Phone and LinkedIn - Two columns */}
      <div className="grid md:grid-cols-2" style={{ gap: '48px', marginBottom: '48px' }}>
        <div>
          <label className="block text-xs uppercase tracking-[0.3em] text-white/40" style={{ marginBottom: '16px' }}>
            Phone Number *
          </label>
          <input
            {...register('phone')}
            type="tel"
            placeholder="+964 xxx xxx xxxx"
            className={cn(
              'w-full border-b bg-transparent text-base text-white outline-none transition-colors placeholder:text-white/30',
              errors.phone ? 'border-red-500' : 'border-white/30 focus:border-white'
            )}
            style={{ paddingTop: '16px', paddingBottom: '16px' }}
          />
          {errors.phone && (
            <p className="mt-2 text-sm text-red-500">{errors.phone.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.3em] text-white/40" style={{ marginBottom: '16px' }}>
            LinkedIn Profile
          </label>
          <input
            {...register('linkedin')}
            type="url"
            placeholder="https://linkedin.com/in/yourprofile"
            className={cn(
              'w-full border-b bg-transparent text-base text-white outline-none transition-colors placeholder:text-white/30',
              errors.linkedin ? 'border-red-500' : 'border-white/30 focus:border-white'
            )}
            style={{ paddingTop: '16px', paddingBottom: '16px' }}
          />
          {errors.linkedin && (
            <p className="mt-2 text-sm text-red-500">{errors.linkedin.message}</p>
          )}
        </div>
      </div>

      {/* Portfolio */}
      <div style={{ marginBottom: '48px' }}>
        <label className="block text-xs uppercase tracking-[0.3em] text-white/40" style={{ marginBottom: '16px' }}>
          Portfolio / Website
        </label>
        <input
          {...register('portfolio')}
          type="url"
          placeholder="https://yourportfolio.com"
          className={cn(
            'w-full border-b bg-transparent text-base text-white outline-none transition-colors placeholder:text-white/30',
            errors.portfolio ? 'border-red-500' : 'border-white/30 focus:border-white'
          )}
          style={{ paddingTop: '16px', paddingBottom: '16px' }}
        />
        {errors.portfolio && (
          <p className="mt-2 text-sm text-red-500">{errors.portfolio.message}</p>
        )}
      </div>

      {/* CV Upload */}
      <div style={{ marginBottom: '48px' }}>
        <label className="block text-xs uppercase tracking-[0.3em] text-white/40" style={{ marginBottom: '16px' }}>
          Upload Your CV *
        </label>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'relative border-2 border-dashed rounded-lg text-center cursor-pointer transition-all duration-300',
            isDragging
              ? 'border-white bg-white/5'
              : cvFile
                ? 'border-green-500/50 bg-green-500/5'
                : cvError
                  ? 'border-red-500/50'
                  : 'border-white/20 hover:border-white/40'
          )}
          style={{ padding: '40px 32px' }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className="hidden"
          />

          {cvFile ? (
            <div className="flex items-center justify-center gap-4">
              <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-left">
                <p className="text-white font-medium">{cvFile.name}</p>
                <p className="text-white/50 text-sm">{(cvFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile();
                }}
                className="ml-4 p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <svg className="w-5 h-5 text-white/60 hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <>
              <svg className="w-12 h-12 mx-auto text-white/40" style={{ marginBottom: '16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-white/60" style={{ marginBottom: '8px' }}>
                Drag and drop your CV here, or <span className="text-white underline">browse</span>
              </p>
              <p className="text-white/40 text-sm">PDF or Word document, max 5MB</p>
            </>
          )}
        </div>
        {cvError && (
          <p className="mt-2 text-sm text-red-500">{cvError}</p>
        )}
      </div>

      {/* Cover Letter / Message */}
      <div style={{ marginBottom: '48px' }}>
        <label className="block text-xs uppercase tracking-[0.3em] text-white/40" style={{ marginBottom: '16px' }}>
          Cover Letter / Additional Information
        </label>
        <textarea
          {...register('message')}
          rows={4}
          placeholder="Tell us about yourself and why you'd be a great fit..."
          className="w-full resize-none border-b bg-transparent text-base text-white outline-none transition-colors placeholder:text-white/30 border-white/30 focus:border-white"
          style={{ paddingTop: '16px', paddingBottom: '16px' }}
        />
      </div>

      {/* Progress Bar */}
      {isSubmitting && uploadProgress > 0 && (
        <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden" style={{ marginBottom: '24px' }}>
          <div
            className="h-full bg-white transition-all duration-300 ease-out"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-center" style={{ marginTop: '24px', marginBottom: '48px' }}>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center justify-center rounded-full border border-white bg-transparent text-base font-medium text-white transition-all duration-300 hover:bg-white hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ paddingLeft: '60px', paddingRight: '60px', paddingTop: '20px', paddingBottom: '20px' }}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Submitting...
            </>
          ) : (
            'Submit Application'
          )}
        </button>
      </div>

      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className="text-center rounded-lg bg-green-500/10 border border-green-500/30" style={{ padding: '32px' }}>
          <svg className="w-12 h-12 mx-auto text-green-500" style={{ marginBottom: '16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-green-500 text-lg font-medium" style={{ marginBottom: '8px' }}>
            {channel === 'mailto' ? 'Your application email is ready' : 'Application submitted'}
          </p>
          <p className="text-white/60">
            {channel === 'mailto'
              ? `Your email app should now be open, addressed to ${company.email}. Attach your CV and send — we do not upload it from this page.`
              : 'Thank you for your interest. We will review your application and get back to you.'}
          </p>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="text-center rounded-lg bg-red-500/10 border border-red-500/30" style={{ padding: '32px' }}>
          <svg className="w-12 h-12 mx-auto text-red-500" style={{ marginBottom: '16px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-500 text-lg font-medium" style={{ marginBottom: '8px' }}>Submission Failed</p>
          <p className="text-white/60">{`Something went wrong. Please email your application to ${company.email}.`}</p>
        </div>
      )}
    </form>
  );
}
